import { Skin, type Rarity } from '#src/__generated__/globalTypes.ts';
import { FRONTEND_ASSET_HOST } from '#src/constants/assets.ts';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { getRarityColor } from '#src/lib/colors.ts';
import type { Color, HexColor, HexColorOnly } from '#src/style/types.ts';

import colorsBoardDecoration from './assets/board/colors.webp';
import stellarBoardDecoration from './assets/board/stellar.webp';
import winterBoardDecoration from './assets/board/winter.webp';
import SET_VOUCHER_ICON from './assets/voucher.svg';

export { SET_VOUCHER_ICON };

const BOARD_MOBILE_DECORATIONS: Record<string, string> = {
  colors: colorsBoardDecoration,
  stellar: stellarBoardDecoration,
  winter: winterBoardDecoration,
};

export const getBoardMobileDecoration = (setSlug: Nullable<string>) =>
  setSlug ? BOARD_MOBILE_DECORATIONS[setSlug] : undefined;

export const getCollectionBackgroundUrl = (
  setSlug: Nullable<string>,
  teamSlug: Nullable<string>
): string | undefined => {
  if (!setSlug || !teamSlug) {
    return undefined;
  }
  return `${FRONTEND_ASSET_HOST}/card_sets/${setSlug}/collections/${teamSlug}.webp`;
};

export const getCollectionBackground = (
  setSlug: Nullable<string>,
  teamSlug: Nullable<string>
) => {
  const url = getCollectionBackgroundUrl(setSlug, teamSlug);
  if (!url) {
    return undefined;
  }
  return {
    background: `url(${url}) top center / cover no-repeat`,
  };
};

export const useCommonGameSet = () => {
  const { cardSet } = useConfigContext();
  // currentSet is empty while a set is ending, so we fall back to nextSet to
  // start showing the upcoming one
  const setToDisplay = cardSet?.currentSet ?? cardSet?.nextSet;

  return {
    slug: setToDisplay?.slug,
    name: setToDisplay?.name,
    endDate: setToDisplay?.endDate && new Date(setToDisplay.endDate),
  };
};

export const useIsCurrentCardSet = () => {
  const commonGameSet = useCommonGameSet();
  return (setSlug: Nullable<string>) => {
    return !!setSlug && setSlug === commonGameSet.slug;
  };
};

const MIX_SHARD_COLOR: Color = 'var(--c-green-400)';

export const getShardColor = (
  rarity: Nullable<Rarity>,
  branding: Nullable<{ colors: { main?: Nullable<string> } }>
): HexColor => {
  if (!rarity) {
    return MIX_SHARD_COLOR;
  }
  return (branding?.colors.main as HexColorOnly) ?? getRarityColor(rarity);
};

export const SKINS = [Skin.STANDARD, Skin.SHINY, Skin.HOLO, Skin.FULL_ART];
